import { ParsedSlotRow, ParsedPatternRow } from './excelParser';

export interface ValidationError {
  row: number;  // 0-based index into the rows array
  field: string;
  message: string;
}

/**
 * Validate parsed slot rows against business rules.
 * Returns an array of validation errors (empty means all valid).
 */
export function validateSlotRows(
  rows: ParsedSlotRow[],
  templateStartDate: string,
  templateEndDate: string
): ValidationError[] {
  const errors: ValidationError[] = [];
  const tStart = new Date(templateStartDate);
  const tEnd = new Date(templateEndDate);

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    // Date required
    if (!row.date) {
      errors.push({ row: i, field: 'date', message: 'Date is required' });
    } else {
      // Valid date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(row.date)) {
        errors.push({ row: i, field: 'date', message: 'Invalid date format. Use YYYY-MM-DD' });
      } else {
        // Date in range
        const d = new Date(row.date);
        if (isNaN(d.getTime())) {
          errors.push({ row: i, field: 'date', message: 'Invalid date' });
        } else if (d < tStart || d > tEnd) {
          errors.push({
            row: i,
            field: 'date',
            message: `Date ${row.date} is outside template range (${templateStartDate} to ${templateEndDate})`,
          });
        }
      }
    }

    // Start time required
    if (!row.startTime) {
      errors.push({ row: i, field: 'startTime', message: 'Start time is required' });
    } else if (!/^\d{2}:\d{2}$/.test(row.startTime)) {
      errors.push({ row: i, field: 'startTime', message: 'Invalid time format. Use HH:mm' });
    }

    // End time required
    if (!row.endTime) {
      errors.push({ row: i, field: 'endTime', message: 'End time is required' });
    } else if (!/^\d{2}:\d{2}$/.test(row.endTime)) {
      errors.push({ row: i, field: 'endTime', message: 'Invalid time format. Use HH:mm' });
    }

    // End time after start time
    if (row.startTime && row.endTime && row.startTime >= row.endTime) {
      errors.push({ row: i, field: 'endTime', message: 'End time must be after start time' });
    }

    // At least one of instructor or topic
    const hasInstructor = row.instructorId || row.instructorName;
    const hasTopic = row.topicMasterId || row.topicName;
    if (!hasInstructor && !hasTopic) {
      errors.push({ row: i, field: 'instructor/topic', message: 'At least one of instructor or topic must be provided' });
    }
  }

  return errors;
}

/**
 * Validate parsed pattern rows against business rules.
 * Returns an array of validation errors (empty means all valid).
 */
export function validatePatternRows(rows: ParsedPatternRow[]): ValidationError[] {
  const errors: ValidationError[] = [];

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];

    // Day of week required and valid
    if (!row.dayOfWeek || row.dayOfWeek < 1 || row.dayOfWeek > 7) {
      errors.push({ row: i, field: 'day', message: 'Day of week is required. Use Mon/Tue/.../Sun or 1-7' });
    }

    // Start time required
    if (!row.startTime) {
      errors.push({ row: i, field: 'startTime', message: 'Start time is required' });
    } else if (!/^\d{2}:\d{2}$/.test(row.startTime)) {
      errors.push({ row: i, field: 'startTime', message: 'Invalid time format. Use HH:mm' });
    }

    // End time required
    if (!row.endTime) {
      errors.push({ row: i, field: 'endTime', message: 'End time is required' });
    } else if (!/^\d{2}:\d{2}$/.test(row.endTime)) {
      errors.push({ row: i, field: 'endTime', message: 'Invalid time format. Use HH:mm' });
    }

    // End time after start time
    if (row.startTime && row.endTime && row.startTime >= row.endTime) {
      errors.push({ row: i, field: 'endTime', message: 'End time must be after start time' });
    }
  }

  return errors;
}

/**
 * Get errors for a specific row index
 */
export function getRowErrors(errors: ValidationError[], rowIndex: number): ValidationError[] {
  return errors.filter(e => e.row === rowIndex);
}

/**
 * Check if a specific row has any errors
 */
export function rowHasErrors(errors: ValidationError[], rowIndex: number): boolean {
  return errors.some(e => e.row === rowIndex);
}
